declare const BranchCodeBrand: unique symbol;

/**
 * Branch code with format: {country_code}_{id}
 */
export type BranchCode = string & { readonly [BranchCodeBrand]: "BranchCode" };

export class BranchCodeError extends Error {
  constructor(
    public readonly kind: "Format" | "InvalidCountryCode" | "InvalidId",
    message: string,
  ) {
    super(message);
  }
}

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/;
const ID_PATTERN = /^[0-9]+$/;
const FULL_PATTERN = /^([A-Z]{2})_([0-9]+)$/;

const fromParts = (id: string, countryCode: string): BranchCode => {
  if (!COUNTRY_CODE_PATTERN.test(countryCode)) {
    throw new BranchCodeError("InvalidCountryCode", `Invalid country code "${countryCode}"`);
  }
  if (!ID_PATTERN.test(id)) {
    throw new BranchCodeError("InvalidId", `Invalid branch code ID "${id}"`);
  }

  return `${countryCode}_${id}` as BranchCode;
};

const fromString = (str: string): BranchCode => {
  const match = FULL_PATTERN.exec(str);

  if (!match) {
    throw new BranchCodeError(
      "Format",
      `String "${str}" is not a valid BranchCode. Expected format: {country_code}_{id}`,
    );
  }

  return str as BranchCode;
};

const toParts = (id: BranchCode): { id: string; countryCode: string } => {
  const str = id as string;
  const match = FULL_PATTERN.exec(str);

  if (match && match[1] && match[2]) {
    return {
      id: match[2],
      countryCode: match[1],
    };
  } else {
    throw new BranchCodeError("Format", `BranchCode "${str}" has invalid format.`);
  }
};

const toString = (id: BranchCode): string => {
  return id as string;
};

export const BranchCode = {
  fromParts,
  fromString,
  toString,
  toParts,
} as const;
